// kfe-app-menu.js — the overflow menu reached from the header hamburger.
//
// Replaces the old header "Settings" text link (Brief 089). Holds the
// secondary destinations that aren't top-level sections: Settings, Sign out,
// Help. The Language switcher lives inside the Settings screen (one tap from
// here), so it isn't duplicated as its own row — Settings is the entry point.
// Help is a present-but-stubbed row (disabled) until its screen exists.
//
// Presentation is breakpoint-driven (design-doc §4):
//   ≤1024px (phone + tablet) — a drawer that slides in from the right, over a
//                              dimmed backdrop.
//   ≥1024px (desktop)        — a dropdown anchored to the top-right button.
// The 1024px breakpoint hardcodes --kfe-bp-desktop (tokens.css) because CSS
// vars can't be used in @media conditions; keep them in sync.
//
// Settings is a real navigation — an <a href> the router intercepts. Sign out
// is NOT: it is a <button> running the full sign-out (server-side JWT
// invalidation, then local clear, then route), because the plain
// <a href="/login"> it used to be cleared nothing — it dropped the learner on
// the login form while localStorage still held a live session and the CRM
// still counted the session as active for the rest of its 2h TTL (Brief 191).
// The menu closes on any selection, on backdrop click, and on Escape.

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";
import { t } from "../core/i18n.js";
import { signOutFull } from "../data/auth-store.js";
import * as router from "../router.js";

export class KFEAppMenu extends KFEComponent {
  static styles = `
    :host { position: relative; display: inline-flex; }

    button.trigger {
      background: transparent; border: 0; color: var(--kfe-fg);
      font-size: 1.5rem; line-height: 1; padding: 0.25rem 0.5rem;
      cursor: pointer;
    }

    .backdrop {
      position: fixed; inset: 0; background: rgba(0,0,0,0.5);
      border: 0; padding: 0; cursor: default; z-index: 40;
    }

    nav.panel {
      position: fixed; top: 0; right: 0; bottom: 0;
      width: min(78vw, 300px);
      background: var(--kfe-bg-header);
      border-left: 1px solid var(--kfe-border);
      padding: 1rem;
      padding-top: max(1rem, env(safe-area-inset-top));
      display: flex; flex-direction: column; gap: 0.25rem;
      z-index: 41;
    }
    nav.panel a, nav.panel span.item, nav.panel button.item {
      display: block; padding: 0.75rem 0.5rem;
      color: var(--kfe-fg); text-decoration: none;
      border-radius: var(--kfe-radius); font-size: 1rem;
    }
    /* Reset the UA button chrome so the Sign out row is visually identical to
       the anchor rows above it (Brief 191 gotcha 3). */
    nav.panel button.item {
      background: transparent; border: 0; margin: 0;
      width: 100%; text-align: left; font: inherit; font-size: 1rem;
      cursor: pointer;
    }
    nav.panel a:hover, nav.panel button.item:hover { background: var(--kfe-surface-hover); }
    nav.panel span.item[aria-disabled="true"] { color: var(--kfe-muted); cursor: not-allowed; }

    /* Desktop: dropdown anchored under the trigger. 1024px === --kfe-bp-desktop. */
    @media (min-width: 1024px) {
      .backdrop { background: transparent; }
      nav.panel {
        position: absolute; top: calc(100% + 0.5rem); right: 0;
        bottom: auto; left: auto;
        width: 220px; height: auto;
        border: 1px solid var(--kfe-border); border-radius: var(--kfe-radius);
        box-shadow: 0 8px 24px rgba(0,0,0,0.4);
        padding: 0.5rem;
      }
    }
  `;

  /** @type {boolean} */ _open = false;

  onConnected() {
    const onKey = (/** @type {KeyboardEvent} */ e) => {
      if (e.key === "Escape" && this._open) this._close();
    };
    document.addEventListener("keydown", onKey);
    this.addCleanup(() => document.removeEventListener("keydown", onKey));

    const onI18n = () => this._scheduleRender();
    document.addEventListener("kfe:i18n", onI18n);
    this.addCleanup(() => document.removeEventListener("kfe:i18n", onI18n));
  }

  _toggle = () => { this._open = !this._open; this._scheduleRender(); };
  _close = () => { this._open = false; this._scheduleRender(); };

  // Brief 191. Same contract as the Settings screen's Sign out row: await the
  // server-side invalidation (which clears the local session either way),
  // then route. signOutFull()'s kfe:auth-changed already bounces the shell to
  // /login; the explicit navigate() is cheap defence-in-depth for the case
  // where this menu is open over an already-public route (e.g. "/") that the
  // bounce leaves alone.
  _signOut = async () => {
    await signOutFull();
    router.navigate("/login");
  };

  render() {
    const trigger = html`
      <button class="trigger" type="button"
        aria-label="${t("nav.menu")}"
        aria-haspopup="menu"
        aria-expanded="${this._open ? "true" : "false"}"
        on:click=${this._toggle}>☰</button>
    `;

    if (!this._open) return trigger;

    return html`
      ${trigger}
      <button class="backdrop" type="button" aria-label="${t("nav.close")}" on:click=${this._close}></button>
      <nav class="panel" role="menu" on:click=${this._close}>
        <a href="/settings" role="menuitem">${t("nav.settings")}</a>
        <span class="item" role="menuitem" aria-disabled="true">${t("nav.help")}</span>
        <button class="item" type="button" role="menuitem"
          on:click=${this._signOut}>${t("settings.signOut")}</button>
      </nav>
    `;
  }
}
